import { Link } from "react-router-dom";
import { t } from "../i18n";
import type { SourceStatus } from "../services/types";
import { Card, fmtRelative, Skeleton } from "./ui";

const GOOD = ["healthy", "unchanged"];
const STATUS: Record<string, string> = {
  healthy: "Healthy", unchanged: "Healthy (no changes)", syncing: "Syncing", delayed: "Delayed", disabled: "Disabled",
  not_configured: "Not configured", authentication_required: "Authentication required", rate_limited: "Rate limited",
  unavailable: "Unavailable", manual_import: "Manual import", import_failed: "Import failed", degraded: "Some feeds failing", configuration_required: "Configuration required",
};
const FRESHNESS: Record<string, [string, string]> = {
  fresh: ["Listings fresh", "text-emerald-600 dark:text-emerald-400"],
  stale: ["Listings stale", "text-amber-600 dark:text-amber-400"],
  empty: ["No active listings", "text-slate-500"],
};

/** Source health kept apart from data freshness: a sync can succeed while listings are old, and the other way round. */
export function SourceHealth({ sources, loading }: { sources?: SourceStatus[]; loading?: boolean }) {
  return (
    <Card title={t("Source health")} actions={<Link to="/settings?tab=drop" className="text-sm text-brand-600 hover:underline">{t("Manage sources")}</Link>} bodyClass="p-0">
      {loading || !sources ? (
        <div className="space-y-3 p-5">{[0, 1].map((i) => <Skeleton key={i} className="h-4 w-full" />)}</div>
      ) : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {sources.map((s) => {
            const [fresh, freshCls] = FRESHNESS[s.freshness] ?? FRESHNESS.empty;
            const dot = !s.enabled ? "bg-slate-300 dark:bg-slate-600" : GOOD.includes(s.status) ? "bg-emerald-500" : s.status === "syncing" ? "bg-brand-500" : "bg-amber-500";
            return (
              <li key={s.id} className="flex flex-col gap-1 px-5 py-3 text-sm sm:flex-row sm:items-center sm:gap-4">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${dot}`} />
                  <span className="truncate font-medium">{s.name}</span>
                  <span className="text-xs text-slate-500">{t(STATUS[s.status] ?? s.status)}</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                  <span title={t("Last successful sync")}>{t("Success")}: {s.last_success ? fmtRelative(s.last_success) : "—"}</span>
                  <span title={t("Last attempt")}>{t("Attempt")}: {s.last_attempt ? fmtRelative(s.last_attempt) : "—"}</span>
                  {s.enabled && <span className={freshCls} title={s.newest_listing ? t("Newest listing seen {when}", { when: fmtRelative(s.newest_listing) }) : undefined}>{t(fresh)}</span>}
                </div>
                {s.enabled && s.last_error && !GOOD.includes(s.status) && <p className="line-clamp-1 text-xs text-red-600 sm:hidden">{s.last_error}</p>}
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
